import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ImagePlus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function LogoUploader() {
  const [brand, setBrand] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/branding").then((r) => setBrand(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Logo must be PNG, JPG or WEBP");
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be under 2 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const { data } = await api.post("/settings/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBrand(data);
      toast.success("Logo saved — notices and PDFs now carry your branding");
      window.dispatchEvent(new Event("branding-updated"));
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/settings/branding/logo");
      setBrand(data);
      toast.success("Logo removed — back to the default VuZuRent header");
      window.dispatchEvent(new Event("branding-updated"));
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  if (!brand) return null;

  return (
    <div className="mt-5 flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="logo-uploader">
      <div className="h-16 w-28 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
        {brand.logo_url
          ? <img src={`${brand.logo_url}?v=${encodeURIComponent(brand.logo_url.length)}`} alt="Business logo" className="max-h-14 max-w-[104px] object-contain" data-testid="logo-preview" />
          : <span className="text-[11px] text-slate-400 text-center px-2" data-testid="logo-empty">No logo yet</span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900">Business logo</div>
        <p className="text-xs text-slate-500 mt-0.5">Shown at the top of notice emails, receipts, statements and the year-end PDF, plus in the app sidebar. PNG/JPG/WEBP, under 2 MB — a wide logo on a transparent or white background looks best.</p>
        <div className="flex gap-2 mt-2.5">
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => ref.current?.click()} disabled={busy} data-testid="logo-upload-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} {brand.logo_url ? "Replace logo" : "Upload logo"}
          </Button>
          {brand.logo_url && (
            <Button size="sm" variant="ghost" className="gap-1.5 text-slate-500 hover:text-rose-600" onClick={remove} disabled={busy} data-testid="logo-remove-btn">
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </Button>
          )}
        </div>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid="logo-input" />
      </div>
    </div>
  );
}
